import { createFileRoute, Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, UserCog, GraduationCap, Users as UsersIcon, HeartHandshake } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/people/Pagination';
import { RoleBadge } from '../components/system/RoleBadge';
import { UserStatusBadge } from '../components/system/UserStatusBadge';
import { InviteUserDrawer } from '../components/system/InviteUserDrawer';
import { UserDetailDrawer } from '../components/system/UserDetailDrawer';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { canInviteAnyone } from '../components/system/inviteRoles';
import { timeAgo } from '../lib/format';
import type { PortalUser, PortalUserLink } from '../components/system/types';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  usersFilterConfig,
  usersSearchSchema,
  USERS_SORT_COLUMNS,
} from '../components/system/usersFilterConfig';

const PAGE_SIZE = 20;

const LINK_TYPE_ROUTE: Record<PortalUserLink['type'], string | null> = {
  teacher: '/people/teachers',
  manager: '/people/managers',
  student: '/people/students',
  household: '/people/households',
};

const LinkIcon = ({ type }: { type: PortalUserLink['type'] }) => {
  const size = 12;
  if (type === 'teacher') return <UsersIcon size={size} aria-hidden="true" />;
  if (type === 'manager') return <UserCog size={size} aria-hidden="true" />;
  if (type === 'student') return <GraduationCap size={size} aria-hidden="true" />;
  return <HeartHandshake size={size} aria-hidden="true" />;
};

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
};

export const Route = createFileRoute('/_authed/system/users')({
  validateSearch: usersSearchSchema,
  component: UsersPage,
});

function UsersPage() {
  const callerRole = useAuthStore((s) => s.user?.primaryRole);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [inviteOpen, setInviteOpen] = useState(false);
  // Drives the existing UserDetailDrawer in edit mode when the pencil
  // column is clicked. The row body navigates to the detail page, so this
  // is reserved for the power-user shortcut only.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The adapter narrows the route's precisely-
  // typed search/navigate surface to the toolkit's structural `FilterRoute`
  // contract; the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigate = Route.useNavigate();
      return (opts) =>
        navigate({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(usersFilterConfig, filterRoute, { pageSize: PAGE_SIZE });
  const { values, apiParams, setSort, setPage } = filters;

  const listQuery = useQuery({
    queryKey: ['users', apiParams] as const,
    queryFn: ({ signal }) => api.users.list(apiParams, signal),
    placeholderData: (prev) => prev,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;
  const canInvite = callerRole ? canInviteAnyone(callerRole) : false;
  // The pencil shortcut opens the rich UserDetailDrawer. Role-change +
  // status mutations inside the drawer are super_admin only, so the
  // shortcut is gated to super_admin to avoid presenting a control that
  // would render a near-empty drawer for other readers. The detail page
  // remains the canonical surface for academic_admin to edit identity.
  const canPencilEdit = callerRole === 'super_admin';

  const columns = useMemo<ColumnDef<PortalUser, unknown>[]>(
    () => {
      const cols: ColumnDef<PortalUser, unknown>[] = [
        {
          id: 'name',
          enableSorting: false,
          header: () => (
            <SortableHeader
              label="Name"
              binding={USERS_SORT_COLUMNS.displayName}
              currentSort={values.sort}
              onSort={setSort}
            />
          ),
          cell: (info) => {
            const row = info.row.original;
            return (
              <span className="flex items-center gap-3 text-left">
                <span
                  aria-hidden="true"
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gold-100 text-xs font-semibold text-gold-900"
                >
                  {initialsOf(row.displayName)}
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-medium text-[color:var(--color-text)]">
                    {row.displayName}
                  </span>
                </span>
              </span>
            );
          },
        },
        {
          id: 'email',
          accessorKey: 'email',
          enableSorting: false,
          header: 'Email',
          cell: (info) => (
            <span className="truncate text-[color:var(--color-text-muted)]">
              {info.row.original.email}
            </span>
          ),
        },
        {
          id: 'role',
          enableSorting: false,
          header: 'Role',
          cell: (info) => <RoleBadge role={info.row.original.primaryRole} />,
        },
        {
          id: 'status',
          enableSorting: false,
          header: 'Status',
          cell: (info) => <UserStatusBadge status={info.row.original.status} />,
        },
        {
          id: 'linked',
          enableSorting: false,
          header: 'Linked',
          cell: (info) => {
            const linked = info.row.original.linked ?? null;
            if (!linked) {
              return <span className="text-xs text-[color:var(--color-text-muted)]">—</span>;
            }
            const href = LINK_TYPE_ROUTE[linked.type];
            const content = (
              <span className="inline-flex items-center gap-1.5 text-xs">
                <LinkIcon type={linked.type} />
                <span className="truncate text-[color:var(--color-text)]">{linked.label}</span>
              </span>
            );
            if (!href) return content;
            return (
              <RouterLink
                to={href}
                onClick={(e) => e.stopPropagation()}
                className="hover:text-gold-700"
              >
                {content}
              </RouterLink>
            );
          },
        },
        {
          id: 'lastLogin',
          enableSorting: false,
          header: () => (
            <SortableHeader
              label="Last login"
              binding={USERS_SORT_COLUMNS.lastLoginAt}
              currentSort={values.sort}
              onSort={setSort}
            />
          ),
          cell: (info) => {
            const v = info.row.original.lastLoginAt;
            return (
              <span className="text-xs text-[color:var(--color-text-muted)]">
                {v ? timeAgo(v) : '—'}
              </span>
            );
          },
        },
      ];

      if (canPencilEdit) {
        cols.push({
          id: 'edit',
          enableSorting: false,
          header: () => <span className="sr-only">Edit</span>,
          cell: (info) => (
            <div className="flex justify-end">
              <EditIconButton
                label={`Edit ${info.row.original.displayName}`}
                onClick={() => setSelectedUserId(info.row.original.id)}
              />
            </div>
          ),
        });
      }

      return cols;
    },
    [canPencilEdit, values.sort, setSort],
  );

  return (
    <ListOrOutlet basePath="/system/users">
    <ListShell
      title="Logins & roles"
      description="Portal accounts and their access."
      primaryAction={
        canInvite ? (
          <Button onClick={() => setInviteOpen(true)} icon={<Plus size={16} aria-hidden="true" />}>
            Invite user
          </Button>
        ) : null
      }
    >
      <FilterBar config={usersFilterConfig} filters={filters} resultCount={total} />

      {listQuery.isError ? (
        <ErrorState
          title="Could not load users"
          description={
            listQuery.error instanceof ApiError
              ? listQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<PortalUser>
            data={items}
            columns={columns}
            isLoading={isLoadingList}
            getRowId={(row) => row.id}
            onRowClick={(row) =>
              void navigate({
                to: '/system/users/$userId',
                params: { userId: row.id },
              })
            }
            emptyState={
              <EmptyState
                title="No users match these filters"
                description={
                  canInvite
                    ? 'Try clearing a filter, or invite your first staff member to get started.'
                    : 'No portal accounts match your filters.'
                }
                action={
                  canInvite ? (
                    <Button
                      onClick={() => setInviteOpen(true)}
                      icon={<Plus size={16} aria-hidden="true" />}
                    >
                      Invite user
                    </Button>
                  ) : null
                }
              />
            }
          />
          <Pagination page={values.page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      <InviteUserDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: ['users'] });
        }}
      />

      <UserDetailDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onChange={() => {
          void queryClient.invalidateQueries({ queryKey: ['users'] });
          if (selectedUserId) {
            void queryClient.invalidateQueries({
              queryKey: ['user', 'detail', selectedUserId],
            });
          }
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}
